import { Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { ConfigService } from '../../services/config/config.service';
import { Config } from '../../models/config';

@Component({
  selector: 'app-config',
  imports: [],
  templateUrl: './config.component.html',
  styleUrls: ['./config.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfigComponent {
  protected readonly configService = inject(ConfigService);

  get config(): Config | null {
    return this.configService.config;
  }

  /**
   * Format a value for display, handling different data types appropriately
   */
  formatValue(value: any): string {
    if (value === null || value === undefined) {
      return 'Not set';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'Empty array';
      }
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  }

  /**
   * Check if a value is an object (but not an array)
   */
  isObject(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  /**
   * Check if a value is an array
   */
  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  /**
   * Get object keys for iteration
   */
  getObjectKeys(obj: any): string[] {
    return Object.keys(obj || {});
  }

  /**
   * Safely get a property value from config using bracket notation
   */
  getConfigValue(config: Config, key: string): any {
    return (config as any)[key];
  }

  /**
   * Get the display name for a configuration key
   */
  getDisplayName(key: string): string {
    // Convert camelCase to Title Case
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  /**
   * Get CSS class for configuration section
   */
  getSectionClass(key: string): string {
    const sectionClasses: { [key: string]: string } = {
      'auth': 'text-primary',
      'signalR': 'text-info',
      'notifications': 'text-warning',
      'scoring': 'text-success',
      'logging': 'text-danger',
      'loading': 'text-muted',
      'toastr': 'text-info'
    };

    return sectionClasses[key] || 'text-default';
  }

  /**
   * Check if a section should be collapsed by default
   */
  shouldCollapseSection(key: string): boolean {
    const collapsibleSections = ['navItems', 'defaults'];
    return collapsibleSections.includes(key);
  }
}
